import React, { useState } from 'react';
import type { RunAnalysisEntity } from '@/types/run-stage.types';
import { useRunAnalysisStore } from '@/run-stage/store/runAnalysisStore';
import { useGraphEditorStore, useSelectedGroupIds } from '@/graph-store';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';

// Icons
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';

import '../styles/RunAnalysisHeader.css';

interface RunAnalysisHeaderProps {
  analysis: RunAnalysisEntity;
}

/**
 * RunAnalysisHeader
 *
 * Shows:
 * - Top line: Design / Plan / Execute / Analysis
 * - Ribbon line: Groups, Highlights, with Status controls on right
 */

interface RibbonGroupProps {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}

const RibbonGroup: React.FC<RibbonGroupProps> = ({ label, children, last }) => (
  <Box
    sx={{
      display: 'flex',
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 1,
        py: 0.75,
        minWidth: 120,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'nowrap',
          mb: 0.5,
        }}
      >
        {children}
      </Box>
    </Box>
    {!last && (
      <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
    )}
  </Box>
);

interface RibbonToggleRowProps {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const RibbonToggleRow: React.FC<RibbonToggleRowProps> = ({ icon, label, checked, onChange }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      width: '100%',
      px: 0.5,
      py: 0.25,
      borderRadius: 0.5,
      cursor: 'pointer',
      '&:hover': { backgroundColor: 'action.hover' },
    }}
    onClick={() => onChange(!checked)}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', color: checked ? 'primary.main' : 'text.secondary' }}>
      {icon}
    </Box>
    <Typography
      variant="caption"
      sx={{
        flex: 1,
        fontSize: '0.72rem',
        fontWeight: checked ? 600 : 400,
        color: checked ? 'text.primary' : 'text.secondary',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <Switch
    size="small"
    checked={checked}
    onClick={(e) => e.stopPropagation()}
    onChange={(e) => onChange(e.target.checked)}
    sx={{ ml: 0.5 }}
/>
  </Box>
);

export const RunAnalysisHeader: React.FC<RunAnalysisHeaderProps> = ({ analysis }) => {
  const { update } = useRunAnalysisStore();
  const [isUpdating, setIsUpdating] = useState(false);

  const isCompleted = analysis.status === 'COMPLETED';
  const isReadOnly = isCompleted || analysis.isArchived;

  // Get highlight state from Zustand store
  const highlights = useGraphEditorStore((s) => s.highlights.visible);
  const toggleHighlightVisible = useGraphEditorStore((s) => s.toggleHighlightVisible);
  const collapseAllGroups = useGraphEditorStore((s) => s.collapseAllGroups);
  const expandAllGroups = useGraphEditorStore((s) => s.expandAllGroups);
  const setGroupsCollapsed = useGraphEditorStore((s) => s.setGroupsCollapsed);
  const selectedGroupIds = useSelectedGroupIds();

  const handleMarkCompleted = async () => {
    try {
      setIsUpdating(true);
      await update({
        _id: analysis._id,
        status: 'COMPLETED' as const,
      });
    } catch (err) {
      console.error('[RunAnalysisHeader] Failed to mark as completed:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleCompleted = () => {
    if (isCompleted) return;
    handleMarkCompleted();
  };

  const toggle = (key: keyof typeof highlights) =>
    toggleHighlightVisible(key);

  return (
    <div className="run-analysis-header">
      {/* Top line: Design / Plan / Execute / Analysis */}
      <div className="header-top-line">
        <span>Design / Plan / Execute / <strong>Analysis</strong></span>
      </div>

      {/* Combined line: Toolbar (left) + Status controls (right) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          py: 0.75,
          borderTop: '1px solid #f0f0f0',
          minHeight: 56,
          gap: 2,
        }}
      >
        {/* Left side: Toolbar (Groups + Highlights) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            overflow: 'auto',
          }}
        >
          {/* Group 1 – Groups */}
          <RibbonGroup label="Groups">
            <Button
              size="small"
              color="inherit"
              startIcon={<UnfoldLessIcon sx={{ fontSize: 15 }} />}
              onClick={selectedGroupIds.length > 0 ? () => setGroupsCollapsed(selectedGroupIds, true) : collapseAllGroups}
              sx={{
                textTransform: 'none',
                fontSize: '0.72rem',
                justifyContent: 'flex-start',
                width: '100%',
                color: 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedGroupIds.length > 0 ? `Collapse Selection (${selectedGroupIds.length})` : 'Collapse Groups'}
            </Button>
            <Button
              size="small"
              color="inherit"
              startIcon={<UnfoldMoreIcon sx={{ fontSize: 15 }} />}
              onClick={selectedGroupIds.length > 0 ? () => setGroupsCollapsed(selectedGroupIds, false) : expandAllGroups}
              sx={{
                textTransform: 'none',
                fontSize: '0.72rem',
                justifyContent: 'flex-start',
                width: '100%',
                color: 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedGroupIds.length > 0 ? `Expand Selection (${selectedGroupIds.length})` : 'Expand Groups'}
            </Button>
          </RibbonGroup>

          {/* Group 2 – Highlights */}
          <RibbonGroup label="Highlights" last>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
              <RibbonToggleRow
                icon={<CommentOutlinedIcon sx={{ fontSize: 15 }} />}
                label="Comments"
                checked={highlights.comments}
                onChange={() => toggle('comments')}
              />
              <RibbonToggleRow
                icon={<NoteAltOutlinedIcon sx={{ fontSize: 15 }} />}
                label="Notes"
                checked={highlights.notes}
                onChange={() => toggle('notes')}
              />
              <RibbonToggleRow
                icon={<FlagOutlinedIcon sx={{ fontSize: 15 }} />}
                label="Checkpoints"
                checked={highlights.checkpoints}
                onChange={() => toggle('checkpoints')}
              />
              <RibbonToggleRow
                icon={<NotificationsNoneIcon sx={{ fontSize: 15 }} />}
                label="Notifications"
                checked={highlights.notifications}
                onChange={() => toggle('notifications')}
              />
              <RibbonToggleRow
                icon={<CheckCircleOutlinedIcon sx={{ fontSize: 15 }} />}
                label="Approvals"
                checked={highlights.approvals}
                onChange={() => toggle('approvals')}
              />
            </Box>
          </RibbonGroup>
        </Box>

        {/* Right side: Status controls (LIVE, Mark Completed, ARCHIVED) */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
          }}
        >
          {!isCompleted && (
            <Box
              sx={{
                padding: '4px 10px',
                borderRadius: '3px',
                fontWeight: 600,
                fontSize: '11px',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                color: 'white',
                backgroundColor: '#28a745',
              }}
            >
              LIVE
            </Box>
          )}

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: isUpdating || isReadOnly ? 'not-allowed' : 'pointer',
              opacity: isUpdating || isReadOnly ? 0.6 : 1,
            }}
            className={isCompleted ? 'mark-completed-checked' : ''}
          >
            <input
              type="checkbox"
              checked={isCompleted}
              onChange={handleToggleCompleted}
              disabled={isUpdating || isReadOnly}
              style={{
                display: 'none',
              }}
            />
            <span className="toggle-switch"></span>
            <span
              style={{
                fontSize: '12px',
                fontWeight: isCompleted ? 600 : 400,
                color: isCompleted ? '#333' : '#666',
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {isCompleted ? 'Completed' : 'Mark Completed'}
            </span>
          </label>

          {analysis.isArchived && (
            <Box
              sx={{
                padding: '4px 10px',
                borderRadius: '3px',
                fontWeight: 600,
                fontSize: '11px',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                color: 'white',
                backgroundColor: '#6c757d',
              }}
            >
              ARCHIVED
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );
};
